import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import type { PlanBlock } from '../../api/bindings/PlanBlock.ts';
import type { SectionWithBlocks } from '../../api/bindings/SectionWithBlocks.ts';
import { usePlan, useUpdateBlock, useUpdatePlan } from '../../api/hooks/usePlans.ts';
import GenerationReview from '../../components/GenerationReview.tsx';
import { BlockEdit } from '../../components/blocks/index.tsx';
import { BLOCK_KIND_LABELS } from '../../components/blocks/types.ts';

const PAGE_TITLES: Record<number, string> = {
  1: 'Business Model Canvas',
  2: 'Supporting Brief',
  3: 'SWOT & Market',
  4: 'Open Items',
};

function SourceBadge({ source }: { source: PlanBlock['source'] }) {
  const styles: Record<PlanBlock['source'], string> = {
    template: 'bg-gray-100 text-gray-500',
    ai: 'bg-violet-100 text-violet-700',
    edited: 'bg-blue-100 text-blue-700',
  };
  const labels: Record<PlanBlock['source'], string> = {
    template: 'empty',
    ai: 'AI draft',
    edited: 'edited',
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${styles[source]}`}>
      {labels[source]}
    </span>
  );
}

function BlockEditor({ planId, block }: { planId: string; block: PlanBlock }) {
  const updateBlock = useUpdateBlock(planId);
  // local draft state: saved explicitly, so half-typed content never hits the server
  const [draft, setDraft] = useState(block.content);
  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(block.content),
    [draft, block.content],
  );

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-500">
          {BLOCK_KIND_LABELS[block.content.kind]}
        </span>
        <div className="flex items-center gap-3">
          <SourceBadge source={block.source} />
          {dirty && (
            <>
              <button
                className="text-sm font-semibold text-blue-600 hover:underline"
                disabled={updateBlock.isPending}
                onClick={() => updateBlock.mutate({ blockId: block.id, content: draft })}
              >
                {updateBlock.isPending ? 'Saving…' : 'Save'}
              </button>
              <button
                className="text-sm text-gray-500 hover:underline"
                onClick={() => setDraft(block.content)}
              >
                Discard
              </button>
            </>
          )}
        </div>
      </div>
      <BlockEdit content={draft} onChange={setDraft} />
      {updateBlock.isError && (
        <p className="mt-2 text-sm text-red-600">Save failed: {updateBlock.error.message}</p>
      )}
      <GenerationReview planId={planId} block={block} />
    </div>
  );
}

function SectionEditor({ planId, section }: { planId: string; section: SectionWithBlocks }) {
  return (
    <section id={section.section.slug} className="mb-8">
      <h3 className="mb-3 text-lg font-semibold text-gray-800">{section.section.title}</h3>
      <div className="space-y-4">
        {section.blocks.map((block) => (
          <BlockEditor key={block.id} planId={planId} block={block} />
        ))}
      </div>
    </section>
  );
}

export default function PlanEditor() {
  const { planId } = useParams<{ planId: string }>();
  const plan = usePlan(planId);
  const updatePlan = useUpdatePlan(planId ?? '');
  const [page, setPage] = useState(1);

  if (plan.isPending) {
    return <main className="p-8 text-center text-sm text-gray-400">Loading…</main>;
  }
  if (plan.isError) {
    return (
      <main className="p-8 text-center text-sm text-red-600">
        Could not load this plan: {plan.error.message}
      </main>
    );
  }

  const doc = plan.data;
  const pages = [...new Set(doc.sections.map((s) => s.section.page))].sort((a, b) => a - b);
  const visible = doc.sections.filter((s) => s.section.page === page);

  return (
    <main className="mx-auto max-w-4xl p-8">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/plans" className="text-sm text-blue-600 hover:underline">
            ← Plans
          </Link>
          <h1 className="text-2xl font-bold tracking-tight">{doc.plan.title}</h1>
          <p className="text-sm text-gray-500">{doc.plan.business_name}</p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <Link className="text-blue-600 hover:underline" to={`/plans/${doc.plan.id}/builder`}>
            Builder
          </Link>
          <Link className="text-blue-600 hover:underline" to={`/plans/${doc.plan.id}/view`}>
            View
          </Link>
          <Link className="text-blue-600 hover:underline" to={`/plans/${doc.plan.id}/share`}>
            Share
          </Link>
          <button
            className="rounded-md border border-gray-300 px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50"
            disabled={updatePlan.isPending}
            onClick={() =>
              updatePlan.mutate({
                status: doc.plan.status === 'published' ? 'draft' : 'published',
              })
            }
          >
            {doc.plan.status === 'published' ? 'Unpublish' : 'Publish'}
          </button>
        </div>
      </header>

      <nav className="mb-8 flex flex-wrap gap-2">
        {pages.map((p) => (
          <button
            key={p}
            onClick={() => setPage(p)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium ${
              p === page ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {p}. {PAGE_TITLES[p] ?? `Page ${p}`}
          </button>
        ))}
      </nav>

      {visible.map((section) => (
        <SectionEditor key={section.section.id} planId={doc.plan.id} section={section} />
      ))}
    </main>
  );
}
